import React from 'react';
import { Row } from 'react-table';
import Header from 'layout/Header';
import { withPlaceholder } from 'HOC';
import { IndependentIraUser, IraAccountsTableUser } from 'services/requests';
import { useModalState } from 'hooks/modals/useModalState';
import {
  useExportIndependentIraUsers,
  useGetIndependentIraUserPrefetch,
  useGetIndependentIraUsers,
} from 'hooks/api/independentIra';
import { useSetData } from 'hooks/cache';
import { useNavigate } from 'hooks/routes';
import { useGetIraUserPrefetch } from 'hooks/api/iraUser';
import { mapCustodianTypeKeyToValue } from 'utils';
import { QueryKeys } from 'constants/queryKeys';
import { PlusIcon_24, ShareIcon_24 } from 'assets/icons';
import Button from 'components/Button';
import SpecifyEmail from '../components/SpecifyEmail';
import RenderTable from '../components/RenderTable';
import { IndependentIraUsersTableSign, mapData } from './IndependentIraTable.columns';
import { isIraUser, mapIndependentIraTypeKeyToValue } from '../IraIndependentIra.utils';
import IndependentIraAccountsMetrics from '../components/IndependetnIraAccountsMetrics/IndependentIraAccountsMetrics';

import messages from './IndependentIraTable.messages';
import styles from './IndependentIraTable.module.css';

const RenderTablewithPlaceholders = withPlaceholder(RenderTable);

export default function IndependentIraTable() {
  const { isModalVisible, openModal, closeModal } = useModalState();

  const {
    data: independentIraUsers,
    isLoading,
    isError,
    refetch,
    isRefetching,
  } = useGetIndependentIraUsers();
  const addIndependentIraUserToCache = useSetData();
  const addIraUserToCache = useSetData();
  const { toIndependentIraInfo, toIraUserDetailsThroughIndependentIraTable } =
    useNavigate();

  const { mutate: exportIndependentIraUsers, isLoading: isProcessing } =
    useExportIndependentIraUsers();

  const onNewIraClick = () => {
    openModal();
  };

  const onEdit = (user: IraAccountsTableUser) => {
    if (isIraUser(user.userProfileType)) {
      toIraUserDetailsThroughIndependentIraTable(user.id.toString());
    } else {
      toIndependentIraInfo(user.id.toString());
    }
  };

  const prefetch = useGetIndependentIraUserPrefetch();
  const iraUserPrefetch = useGetIraUserPrefetch();

  const onIndependentIraUserClick = (row: Row<IndependentIraUsersTableSign>): void => {
    const independentIraUser = independentIraUsers?.items[row.index];
    if (!independentIraUser) {
      return;
    }
    if (isIraUser(independentIraUser.userProfileType)) {
      addIraUserToCache(
        {
          ...independentIraUser,
          custodianType: mapCustodianTypeKeyToValue(independentIraUser.custodianType),
        },
        QueryKeys.iraUser,
      );
      toIraUserDetailsThroughIndependentIraTable(String(row.original.id));
    } else {
      addIndependentIraUserToCache(
        {
          ...independentIraUser,
          profileType: mapIndependentIraTypeKeyToValue(
            independentIraUser.userProfileType,
          ),
          custodianType: mapCustodianTypeKeyToValue(independentIraUser.custodianType),
        } as IndependentIraUser,
        QueryKeys.independentIraUser,
      );
      toIndependentIraInfo(String(row.original.id));
    }
  };

  const items = mapData(independentIraUsers?.items, onEdit, prefetch, iraUserPrefetch);

  const onExportCsvClick = async (): Promise<void> => {
    exportIndependentIraUsers();
  };

  return (
    <>
      <Header title={messages.title}>
        <Button
          className={styles.iraAccounts__buttonExportCsv}
          onClick={onExportCsvClick}
          variant={'outline'}
          isLoading={isProcessing}
          disabled={isProcessing}
        >
          <ShareIcon_24 className={styles.iraAccounts__iconExportCsv} />
          {messages.exportCsvButton}
        </Button>
        <Button className={styles.iraAccounts__buttonNewIra} onClick={onNewIraClick}>
          <PlusIcon_24 className={styles.iraAccounts__iconNewIra} />
          {messages.newIraButton}
        </Button>
      </Header>
      <SpecifyEmail onRequestClose={closeModal} isOpen={isModalVisible} />
      <IndependentIraAccountsMetrics />
      <RenderTablewithPlaceholders
        independentIraUsers={independentIraUsers}
        items={items}
        onIraUserClick={onIndependentIraUserClick}
        refetch={refetch}
        isRefetching={isRefetching}
        isLoading={isLoading}
        isError={isError}
        isData={!!independentIraUsers?.context && !!items}
      />
    </>
  );
}
